import { toRaw } from 'vue';
import { RouteLocationRaw, Router } from 'vue-router';

import { Keyboard } from '@capacitor/keyboard';
import CryptoJS from 'crypto-js';
import { memoize } from 'memoize-utils';
import { useLoadingStore } from 'src/stores/loading';
import { useUserInterfaceStateStore } from 'src/stores/userInterfaceState';

import {
	folderExplorerReturnRoute,
	routeHasFolderExplorerSource,
} from 'src/components/workspace/menu/folderExplorerReturnNavigation';

import { DateRangeSelection } from 'src/models/common';
import { ModuleNameEnum } from 'src/models/module/enums';

import { ACTIVE_INSTANCE_AUTHENTICATION_NAME_KEY } from 'src/services/authentication';

import { formatDate } from 'src/util/date';
import { EventBus } from 'src/util/eventBus/EventBus';
import { devBackend } from 'src/util/devBackend';
import { isRunningInNativeCapacitorApp } from 'src/util/platform/browserInteractionCapabilities';

export const encodeBase64 = memoize((str: string): string => {
	return window.btoa(unescape(encodeURIComponent(str)));
});

export const decodeBase64 = memoize((str: string): string => {
	return decodeURIComponent(escape(window.atob(str)));
});
export const urlForInstance = async (instance: string): Promise<string> => {
	if (instance !== null) {
		instance = instance.toLowerCase();
		const pinned = devBackend();
		if (pinned !== null) {
			return instance === pinned.instance ? pinned.origin : '';
		}
		if (instance.length > 0) {
			const comDomainExits = await domainExists(`https://${instance}.infra-struktur.com`);
			const euDomainExits = await domainExists(`https://${instance}.infra-struktur.eu`);

			if (comDomainExits) {
				return `https://${instance}.infra-struktur.com`;
			} else if (euDomainExits) {
				return `https://${instance}.infra-struktur.eu`;
			}
		}
	}
	return '';
};

export const domainExists = (hostname: string): Promise<boolean> => {
	if (hostname.length > 0) {
		return fetch(hostname, {
			headers: {
				Accept: 'text/html',
			},
			method: 'get',
			redirect: 'manual',
		})
			.then((response) => {
				return response.ok || response.type === 'opaqueredirect';
			})
			.catch(async () => {
				return await fetch(hostname, {
					method: 'get',
					mode: 'no-cors',
					redirect: 'manual',
				})
					.then((response) => {
						return response.type === 'opaque' || response.type === 'opaqueredirect';
					})
					.catch(() => {
						return false;
					});
			});
	}
	return Promise.resolve(false);
};

export function isNumber(str: number | string): boolean {
	if (typeof str !== 'string') {
		return false;
	}

	if (str.trim() === '') {
		return false;
	}

	return !Number.isNaN(Number(str));
}

export function getRoundedDateTime(minutes: number, d = new Date()): Date {
	const ms = 1000 * 60 * minutes;
	return new Date(Math.round(d.getTime() / ms) * ms);
}

export const splitHostname = memoize(
	(
		hostname: string
	):
		| {
				domain: string;
				subdomain: string;
				type: string;
		  }
		| undefined => {
		hostname = hostname.replace('https://www.', '');
		hostname = hostname.replace('http://www.', '');
		hostname = hostname.replace('https://', '');
		hostname = hostname.replace('http://', '');

		const urlParts = /([a-z-0-9]{2,63}).([a-z.]{2,5})$/.exec(hostname);
		if (!urlParts) return;

		const [, domain, type] = urlParts;
		const subdomain = hostname.replace(`${domain}.${type}`, '').slice(0, -1);

		return {
			domain,
			subdomain,
			type,
		};
	}
);

export function elementReady<DesiredElementType extends Element = HTMLElement>(
	selector: string
): Promise<DesiredElementType> {
	return new Promise((resolve) => {
		const matchingElement = document.querySelector<DesiredElementType>(selector);
		if (matchingElement) {
			resolve(matchingElement);
			return;
		}

		const observer = new MutationObserver(() => {
			const nextMatchingElement = document.querySelector<DesiredElementType>(selector);
			if (nextMatchingElement) {
				resolve(nextMatchingElement);
				observer.disconnect();
			}
		});

		observer.observe(document.documentElement, {
			childList: true,
			subtree: true,
		});
	});
}

export function registerStatusTapScrollToTop(target: (() => HTMLElement | null | undefined) | string): () => void {
	const handleStatusTap = () => {
		if (typeof target === 'function') {
			const el = target();
			if (el) {
				el.scrollTo({ behavior: 'smooth', top: 0 });
			}
			return;
		}

		void elementReady<HTMLElement>(target).then((scrollableElement) => {
			scrollableElement.scrollTo({
				behavior: 'smooth',
				top: 0,
			});
		});
	};

	window.addEventListener('statusTap', handleStatusTap);

	return () => {
		window.removeEventListener('statusTap', handleStatusTap);
	};
}

export const hex2bin = (hex: string): Uint8Array => {
	const bytes = new Uint8Array(hex.length / 2);
	for (let i = 0; i !== bytes.length; i++) {
		bytes[i] = parseInt(hex.substr(i * 2, 2), 16);
	}
	return bytes;
};

export function bin2hex(bin: string): string {
	return Array.from(bin)
		.map(function (x) {
			return x.toString().padStart(2, '0');
		})
		.join('');
}

export function toRawDeep<T>(observed: T): T {
	const val = toRaw(observed);

	if (Array.isArray(val)) {
		return val.map(toRawDeep) as T;
	}

	if (val === null) return null as T;

	if (typeof val === 'object') {
		const entries = Object.entries(val as Record<string, unknown>).map(
			([key, value]) => [key, toRawDeep(value)] as const
		);

		return Object.fromEntries(entries) as T;
	}

	return val;
}

export function stringToHex(inputString: string): string {
	return inputString
		.split('')
		.map((c) => c.charCodeAt(0).toString(16).padStart(2, '0'))
		.join('');
}

export function getDatesAsString(dates: DateRangeSelection | null | string | undefined): Array<string> {
	const realDates: Array<string> = [];

	if (dates === null || dates === undefined) {
		return realDates;
	}

	if (typeof dates === 'string') {
		realDates.push(dates);
	} else {
		if (!dates.from || !dates.to) {
			return realDates;
		}

		let currentDate = dates.from;
		realDates.push(currentDate);

		const stopDate = dates.to;

		while (currentDate < stopDate) {
			const currentDateAsDate = new Date(currentDate);
			currentDateAsDate.setDate(currentDateAsDate.getDate() + 1);
			currentDate = formatDate(currentDateAsDate, 'YYYY-MM-DD');
			realDates.push(currentDate);
		}
	}

	return [...new Set(realDates)];
}

export function isDateOlderThanHours(date: Date, hours: number): boolean {
	const currentTime = new Date();
	const timeDifferenceInMilliseconds = currentTime.getTime() - date.getTime();
	const timeDifferenceInHours = timeDifferenceInMilliseconds / (1000 * 60 * 60);

	return timeDifferenceInHours > hours;
}

const RESTORE_ACTIVITY_LIST_SCROLL_EVENT = 'restoreTheActivityListScrollPostionAndGrouping';

let activityListScrollRestoreRequested = false;

export function requestActivityListScrollRestore(): void {
	activityListScrollRestoreRequested = true;
}

export function activityListScrollRestoreIsRequested(): boolean {
	return activityListScrollRestoreRequested;
}

export function clearActivityListScrollRestoreRequest(): void {
	activityListScrollRestoreRequested = false;
}

function emitActivityListScrollRestoreAfterRender(eventBus: EventBus): void {
	setTimeout(() => {
		eventBus.emit(RESTORE_ACTIVITY_LIST_SCROLL_EVENT);
		clearActivityListScrollRestoreRequest();
	}, 0);
}

export function restoreActivityListScrollAfterRouterBack(router: Router, eventBus: EventBus): void {
	requestActivityListScrollRestore();

	const removeAfterEachHook = router.afterEach(() => {
		removeAfterEachHook();
		emitActivityListScrollRestoreAfterRender(eventBus);
	});

	router.go(-1);
}

export function restoreActivityListScrollAfterRouterPush(
	router: Router,
	eventBus: EventBus,
	to: RouteLocationRaw
): void {
	requestActivityListScrollRestore();

	void router.push(to).finally(() => {
		emitActivityListScrollRestoreAfterRender(eventBus);
	});
}

function hasVueRouterManagedHistoryPredecessor(): boolean {
	if (typeof window === 'undefined') {
		return false;
	}

	const historyState: unknown = window.history.state;
	return (
		typeof historyState === 'object' &&
		historyState !== null &&
		typeof (historyState as { back?: unknown }).back === 'string'
	);
}

export function back(router: Router, _store: unknown, eventBus: EventBus): void {
	void _store;
	const userInterfaceStateStore = useUserInterfaceStateStore();

	if (isRunningInNativeCapacitorApp()) {
		void Keyboard.hide().then(() => {
			userInterfaceStateStore.setKeyboardIsVisibleState(false);
			if (routeHasFolderExplorerSource(router.currentRoute.value)) {
				if (hasVueRouterManagedHistoryPredecessor()) {
					restoreActivityListScrollAfterRouterBack(router, eventBus);
				} else {
					restoreActivityListScrollAfterRouterPush(router, eventBus, folderExplorerReturnRoute());
				}
				return;
			}

			if (hasVueRouterManagedHistoryPredecessor()) {
				restoreActivityListScrollAfterRouterBack(router, eventBus);
			} else {
				restoreActivityListScrollAfterRouterPush(router, eventBus, '/');
			}
		});
	} else {
		userInterfaceStateStore.setKeyboardIsVisibleState(false);
		if (routeHasFolderExplorerSource(router.currentRoute.value)) {
			if (hasVueRouterManagedHistoryPredecessor()) {
				restoreActivityListScrollAfterRouterBack(router, eventBus);
			} else {
				restoreActivityListScrollAfterRouterPush(router, eventBus, folderExplorerReturnRoute());
			}
			return;
		}

		if (hasVueRouterManagedHistoryPredecessor()) {
			restoreActivityListScrollAfterRouterBack(router, eventBus);
		} else {
			restoreActivityListScrollAfterRouterPush(router, eventBus, '/');
		}
	}
}

export function replaceTheRoute(router: Router, to: RouteLocationRaw) {
	void router.replace(to);
}

export function addToHistoryWithoutVueRouter(router: Router, to: RouteLocationRaw) {
	replaceTheRoute(router, to);
}

export enum LoadingIndicatorTypeEnum {
	DOWNLOAD = 'DOWNLOAD',
	REFRESH = 'REFRESH',
	REFRESH_IDO_HISTORY = 'REFRESH_IDO_HISTORY',
	SEARCH = 'SEARCH',
	STATE_CHANGE = 'STATE_CHANGE',
}

export function instanceSpecificLocalStorageKey(storageKey: string): string {
	const instanceName = localStorage.getItem(ACTIVE_INSTANCE_AUTHENTICATION_NAME_KEY) as string;
	return `${instanceName}-${storageKey}`;
}

export function showTheLoadingIndicator(t: (param: string) => string, type: LoadingIndicatorTypeEnum): void {
	const loadingStore = useLoadingStore();

	switch (type) {
		case LoadingIndicatorTypeEnum.REFRESH:
			loadingStore.show({
				group: 'module',
				message: t('app_loading_indicator_refreshing'),
			});
			break;

		case LoadingIndicatorTypeEnum.REFRESH_IDO_HISTORY:
			loadingStore.show({
				group: 'ido',
				message: t('app_loading_indicator_refreshing'),
			});
			break;

		case LoadingIndicatorTypeEnum.SEARCH:
			loadingStore.show({
				group: 'module',
				message: t('app_loading_indicator_searching'),
			});
			break;

		case LoadingIndicatorTypeEnum.DOWNLOAD:
			loadingStore.show({
				group: 'module',
				message: t('app_loading_indicator_downloading'),
			});
			break;

		case LoadingIndicatorTypeEnum.STATE_CHANGE:
			loadingStore.show({
				group: 'module',
				message: t('app_loading_indicator_state_changing'),
			});
			break;
	}
}

export function hideTheLoadingIndicator(): void {
	useLoadingStore().hide();
}

export function translatedModuleName(t: (param: string) => string, moduleName: string): string {
	let translatedModuleName = '';
	switch (moduleName) {
		case ModuleNameEnum.MODULE_ACTIVITY: {
			translatedModuleName = t('common_module_activity');
			break;
		}

		case ModuleNameEnum.MODULE_APPOINTMENT: {
			translatedModuleName = t('common_module_appointment');
			break;
		}

		case ModuleNameEnum.MODULE_DOCUMENT: {
			translatedModuleName = t('common_module_document');
			break;
		}

		case ModuleNameEnum.MODULE_EMAIL: {
			translatedModuleName = t('common_module_mail');
			break;
		}

		case ModuleNameEnum.MODULE_IDO: {
			translatedModuleName = t('common_module_dataset');
			break;
		}

		case ModuleNameEnum.MODULE_MESSAGE: {
			translatedModuleName = t('common_module_message');
			break;
		}

		case ModuleNameEnum.MODULE_NOTE: {
			translatedModuleName = t('common_module_note');
			break;
		}

		case ModuleNameEnum.MODULE_PHONECALL: {
			translatedModuleName = t('common_module_phonecall');
			break;
		}

		case ModuleNameEnum.MODULE_TASK: {
			translatedModuleName = t('common_module_task');
			break;
		}
	}

	return translatedModuleName;
}

export async function hashSha512(input: string): Promise<string> {
	if (typeof crypto !== 'undefined' && crypto.subtle) {
		try {
			const encoder = new TextEncoder();
			const data = encoder.encode(input);
			const hashBuffer = await crypto.subtle.digest('SHA-512', data);
			const hashArray = Array.from(new Uint8Array(hashBuffer));
			const hashHex = hashArray.map((byte) => byte.toString(16).padStart(2, '0')).join('');
			return hashHex;
		} catch (error) {
			console.warn('Web Crypto API failed, falling back to crypto-js:', error);
		}
	}

	const hash = CryptoJS.SHA512(input);
	return hash.toString(CryptoJS.enc.Hex);
}
